import {
  isTransientNumericValue,
  parseTransientAwareNumber,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/numeric-input';
import { useCallback, useEffect, useRef, useState } from 'react';

const DISPLAY_DECIMAL_PLACES = 2;
const DISPLAY_ROUNDING_FACTOR = 10 ** DISPLAY_DECIMAL_PLACES;

export const roundRangeBoundValue = (value: number): number =>
  Math.round(value * DISPLAY_ROUNDING_FACTOR) / DISPLAY_ROUNDING_FACTOR;

export const formatRangeBoundText = (value: number | null): string =>
  value === null ? '' : String(roundRangeBoundValue(value));

interface UsePricingFilterRangeInputResult {
  handleBlur: () => void;
  handleChangeText: (text: string) => void;
  text: string;
}

/**
 * One Min/Max text field over a nullable numeric bound. Typing updates display
 * text only; blur commits, reverting transient text ('-', '.', '-.'), a value
 * that would put this bound past `otherBoundValue`, and no-opping if untouched.
 * An external `boundValue` change resyncs immediately, even mid-edit.
 */
export const usePricingFilterRangeInput = (
  boundValue: number | null,
  otherBoundValue: number | null,
  boundType: 'max' | 'min',
  onCommit: (value: number | null) => void,
): UsePricingFilterRangeInputResult => {
  const [text, setText] = useState(() => formatRangeBoundText(boundValue));
  const isDirtyRef = useRef(false);

  useEffect(() => {
    isDirtyRef.current = false;
    setText(formatRangeBoundText(boundValue));
  }, [boundValue]);

  const handleChangeText = useCallback((nextText: string) => {
    isDirtyRef.current = true;

    if (nextText === '') {
      setText(nextText);
      return;
    }

    if (isTransientNumericValue(nextText)) {
      setText(nextText);
      return;
    }

    const parsed = parseTransientAwareNumber(nextText);
    if (parsed === null) {
      return;
    }

    setText(nextText);
  }, []);

  const handleBlur = useCallback(() => {
    if (!isDirtyRef.current) {
      return;
    }
    isDirtyRef.current = false;

    if (text !== '' && isTransientNumericValue(text)) {
      setText(formatRangeBoundText(boundValue));
      return;
    }

    const parsed = text === '' ? null : parseTransientAwareNumber(text);
    const crossesOtherBound =
      parsed !== null &&
      otherBoundValue !== null &&
      (boundType === 'min'
        ? parsed > otherBoundValue
        : parsed < otherBoundValue);

    if (crossesOtherBound) {
      setText(formatRangeBoundText(boundValue));
      return;
    }

    onCommit(parsed);
    setText(formatRangeBoundText(parsed));
  }, [boundValue, boundType, onCommit, otherBoundValue, text]);

  return { handleBlur, handleChangeText, text };
};
